/**
 * TASK-829 — the CIO house view, readable from the frontend for the first time.
 *
 * Before this there was no CIO surface anywhere in the product: no service, no hook, no
 * widget, no tab. The frontend's entire knowledge of the house view was a single
 * `cio_view: string | null` leaking through `portfolioFitService`'s holdings, plus
 * `source: 'cio'` on a Radar row — while `cio_stale_sell` asked the advisor to act on that
 * view and every swap candidate the system proposes is a CIO BUY.
 */

import { httpClient } from '../lib/httpClient';

export interface CioVintage {
  /** The snapshot the house view speaks from. */
  as_of: string | null;
  age_days: number | null;
  /**
   * `fresh` | `stale` | `unavailable`. The SAME verdict the radar evaluator uses to decide
   * whether CIO subjects may be cleared (`shared/portfolio/cio_vintage`) — deliberately not a
   * second definition, because one surface calling the view current while another refuses to
   * act on it is the defect TASK-828 is about, in a different place.
   *
   * `stale` and `unavailable` are NOT the same: one means the house view has not been
   * refreshed, the other that there is none.
   */
  status: 'fresh' | 'stale' | 'unavailable' | string;
}

export interface CioHolding {
  isin: string;
  security_name: string | null;
  market_value: number | null;
  sub_asset_class: string | null;
  industry_group: string | null;
  /** A coverage FACT, not a null to render as a dash: the universe does not cover everything
   *  a client holds, and omitting these would let an advisor conclude otherwise. */
  rated: boolean;
  rating: string | null;
  cio_view: string | null;
  issuer: string | null;
  rating_since: string | null;
}

export interface CioHoldingsResponse {
  client_id: string;
  holdings: CioHolding[];
  rated_count: number;
  unrated_count: number;
}

export interface CioCandidate {
  isin: string;
  issuer: string | null;
  valor: string | null;
  mic: string | null;
  yahoo_ticker: string | null;
  cio_view: string | null;
  sub_asset_class: string | null;
  industry_group: string | null;
  value_tags: string[];
  /** `null` = not assessed (no client was named), which is a different answer from `false`. */
  clears_exclusions: boolean | null;
  fit_gain: number | null;
}

export interface CioCandidatesResponse {
  candidates: CioCandidate[];
  client_id: string | null;
  sub_asset_class: string | null;
  industry_group: string | null;
}

export const cioService = {
  getVintage() {
    return httpClient.get<CioVintage>('/api/cio/vintage');
  },
  getHoldings(clientId: string) {
    return httpClient.get<CioHoldingsResponse>(`/api/cio/holdings/${clientId}`);
  },
  getCandidates(params: { sac?: string; ig?: string; clientId?: string }) {
    const q = new URLSearchParams();
    if (params.sac) q.set('sac', params.sac);
    if (params.ig) q.set('ig', params.ig);
    if (params.clientId) q.set('client_id', params.clientId);
    const qs = q.toString();
    return httpClient.get<CioCandidatesResponse>(`/api/cio/candidates${qs ? `?${qs}` : ''}`);
  },
};
